import type { CourseStatus, PlanCourse, RequirementGroup } from '@teachur/http-client';
import type { BadgeVariant } from '@new/common/ui/Badge/Badge';

export interface GroupedPlan {
  generalEducation: RequirementGroup[];
  required: RequirementGroup[];
  electives: RequirementGroup[];
}

export const sumCredits = (courses: PlanCourse[]): number =>
  courses.reduce((total, course) => total + (Number(course.credits) || 0), 0);

const STATUS_BADGE: Record<CourseStatus, { text: string; variant: BadgeVariant } | null> = {
  not_started: null,
  started: { text: 'In progress', variant: 'orange' },
  paused: { text: 'In progress', variant: 'orange' },
  passed: { text: 'Completed', variant: 'green' },
};

export const statusBadge = (status: CourseStatus): { text: string; variant: BadgeVariant } | null =>
  STATUS_BADGE[status];

/** DOM id for a requirement group's card/section — lets the progress bar scroll a click
 * straight to that category once the plan is expanded. */
export const groupAnchorId = (groupId: string): string => `course-builder-group-${groupId}`;

export const groupPlan = (plan: { groups: RequirementGroup[] }): GroupedPlan => ({
  generalEducation: plan.groups.filter((g) => g.kind === 'general_education'),
  required: plan.groups.filter((g) => g.kind === 'required'),
  electives: plan.groups.filter((g) => g.kind === 'elective'),
});

/** A stable identifier for a plan course (used as React key + de-dup key). */
export const courseKey = (course: PlanCourse): string =>
  course.source === 'transfer'
    ? `transfer-${course.transcriptCourseId}`
    : `newlane-${course.courseId}`;

/** A short React list key for a plan course within a section. */
export const rowKey = (course: PlanCourse): string =>
  course.source === 'transfer' ? `t-${course.transcriptCourseId}` : `n-${course.courseId}`;

/** Flattens every group's courses into the save payload (deduped by key). */
export const toSavePayload = (
  groups: RequirementGroup[]
): {
  courses: { courseId: number | null; transcriptCourseId: number | null; groupId: string }[];
} => {
  const seen = new Set<string>();
  const courses: {
    courseId: number | null;
    transcriptCourseId: number | null;
    groupId: string;
  }[] = [];
  for (const group of groups) {
    for (const course of group.courses) {
      const key = courseKey(course);
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      courses.push({
        courseId: course.courseId,
        transcriptCourseId: course.transcriptCourseId,
        groupId: group.id,
      });
    }
  }
  return { courses };
};
